/**
 * skillGraph.js — Feb 2026 · curriculum-agnostic skill graph.
 *
 * A skill = one atomic thing a learner can master (e.g. "recall 2×
 * multiplication table" or "identify main idea of a paragraph").
 * Each skill carries:
 *
 *   · id            — stable slug, e.g. "num-mult-2-times"
 *   · subject       — canonical subject bucket (english / maths / …)
 *   · difficulty    — 1-9 band (roughly age minus 4)
 *   · prerequisites — array of skill ids that must be mastered first
 *   · frameworks    — mapping of framework id → outcome id, e.g.
 *                     { "uk-ks2": "uk-ks2-ma-2",
 *                       "cam-prim": "cam-prim-ma-3",
 *                       "cbse":     "cbse-ma-2b" }
 *
 * The graph is intentionally sparse — this file ships **the seed
 * subset** so the model + validator ship together. The larger
 * migration lives in a follow-up PR (`docs/reports/skill-graph.md`
 * lists the outstanding subjects).
 *
 * Views:
 *   · `skillsForFramework("uk-ks2")` → array of skills with an
 *     outcome mapping to that framework.
 *   · `frameworkYear("uk-ks2", 7)`   → the outcomes a Year-3 learner
 *     would tackle, derived from difficulty bands, NOT stored.
 */

export const SKILLS = [
  // ─── English · Reading foundations ────────────────────────────
  {
    id: "en-phonics-cvc",
    subject: "english",
    label: "Blend consonant-vowel-consonant words",
    difficulty: 1,
    prerequisites: ["en-letter-sounds"],
    frameworks: {
      "uk-ks1": "uk-ks1-en-1",
      "cam-prim": "cam-prim-en-1",
      "ib-pyp": "ib-pyp-en-1",
      "uae-moe": "uae-moe-en-1",
    },
  },
  {
    id: "en-letter-sounds",
    subject: "english",
    label: "Say the sound each letter makes",
    difficulty: 1,
    prerequisites: [],
    frameworks: {
      "uk-ks1": "uk-ks1-en-1",
      "cam-prim": "cam-prim-en-1",
    },
  },
  {
    id: "en-main-idea",
    subject: "english",
    label: "Identify the main idea of a paragraph",
    difficulty: 3,
    prerequisites: ["en-phonics-cvc"],
    frameworks: {
      "uk-ks2": "uk-ks2-en-1",
      "cam-prim": "cam-prim-en-2",
      "common-core": "cc-en-2",
    },
  },
  // ─── Maths · Number foundations ────────────────────────────────
  {
    id: "num-counting-20",
    subject: "maths",
    label: "Count objects to 20",
    difficulty: 1,
    prerequisites: [],
    frameworks: {
      "uk-ks1": "uk-ks1-ma-1",
      "cam-prim": "cam-prim-ma-1",
      "ib-pyp": "ib-pyp-ma-1",
    },
  },
  {
    id: "num-place-value-100",
    subject: "maths",
    label: "Read, write and compare numbers to 100",
    difficulty: 2,
    prerequisites: ["num-counting-20"],
    frameworks: {
      "uk-ks1": "uk-ks1-ma-1",
      "uk-ks2": "uk-ks2-ma-1",
      "cam-prim": "cam-prim-ma-1",
    },
  },
  {
    id: "num-mult-2-times",
    subject: "maths",
    label: "Recall the 2× multiplication table",
    difficulty: 3,
    prerequisites: ["num-place-value-100"],
    frameworks: {
      "uk-ks2": "uk-ks2-ma-2",
      "cam-prim": "cam-prim-ma-3",
      "cbse": "cbse-ma-2b",
    },
  },
  // ─── Science · Living things ───────────────────────────────────
  {
    id: "sci-plant-parts",
    subject: "science",
    label: "Name the parts of a plant and what they do",
    difficulty: 2,
    prerequisites: [],
    frameworks: {
      "uk-ks1": "uk-ks1-sc-1",
      "cam-prim": "cam-prim-sc-1",
      "ib-pyp": "ib-pyp-sc-1",
      "uae-moe": "uae-moe-sc-1",
    },
  },
  {
    id: "sci-hypothesis",
    subject: "science",
    label: "Form a hypothesis and design a fair test",
    difficulty: 5,
    prerequisites: ["sci-plant-parts"],
    frameworks: {
      "uk-ks2": "uk-ks2-sc-4",
      "uk-ks3": "uk-ks3-sc-4",
      "cam-lsec": "cam-lsec-sc-1",
      "common-core": "cc-sc-4",
    },
  },
  // ─── Geography · Locational knowledge ──────────────────────────
  {
    id: "geo-continents",
    subject: "geography",
    label: "Name and locate the seven continents",
    difficulty: 2,
    prerequisites: [],
    frameworks: {
      "uk-ks1": "uk-ks1-geo-1",
      "cam-prim": "cam-prim-geo-1",
      "ib-pyp": "ib-pyp-geo-1",
    },
  },
  {
    id: "geo-climate-change",
    subject: "geography",
    label: "Explain weather and climate systems, and climate change",
    difficulty: 7,
    prerequisites: ["geo-continents"],
    frameworks: {
      "uk-ks3": "uk-ks3-geo-5",
      "cam-lsec": "cam-lsec-geo-1",
    },
  },
];

/** Look up a skill by id. */
export function getSkill(id) {
  return SKILLS.find((s) => s.id === id) || null;
}

/** All skills that map to at least one outcome in a given framework. */
export function skillsForFramework(frameworkId) {
  return SKILLS.filter((s) => s.frameworks && s.frameworks[frameworkId]);
}

/** Derive a "year N" view over the graph for a framework. Age input
 *  is the learner's age. Difficulty band is roughly `age - 4`. */
export function frameworkYear(frameworkId, age) {
  const target = Math.max(1, Math.min(9, Math.round((age || 8) - 4)));
  return skillsForFramework(frameworkId)
    .filter((s) => s.difficulty === target)
    .map((s) => ({ ...s, outcome: s.frameworks[frameworkId] }));
}

/** Build a topological order of skills — prerequisites first. Useful
 *  for the Learning Science Spine (masteryEngine) to serve a "next
 *  skill" list. */
export function topoOrder(subject = null) {
  const set = SKILLS.filter((s) => !subject || s.subject === subject);
  const seen = new Set();
  const out = [];
  function visit(s) {
    if (seen.has(s.id)) return;
    seen.add(s.id);
    (s.prerequisites || []).forEach((p) => {
      const parent = set.find((x) => x.id === p);
      if (parent) visit(parent);
    });
    out.push(s);
  }
  set.forEach(visit);
  return out;
}
